import React, { useEffect, useRef, useState } from "react"
import _ from 'lodash'
import moment from 'moment'
import Isvg from 'react-inlinesvg'
import { RouteComponentProps } from '@reach/router'
// PR
import { DataTable } from "primereact/datatable"
import { Column } from "primereact/column"
// Components
import { field as _field } from "components/table/lib/column-field"
import ConfirmationModal from "components/modal/confirmation-modal"
import SearchHeader from "components/table/search-header"
import { Toolbar } from "components/subnav"
import CreateSnapshot from 'components/snapshots/create-snapshot'
import UploadSnapshot from 'components/snapshots/upload-snapshot'
import { WithTip } from 'components/tooltip/with-tip'
// Data
import api_match from "data/interface/api"
import keyService, * as chord from "data/key"
import { local } from 'data/lib/local-json'
import propertyService from 'data/property'
import snapshotService from 'data/snapshots'
// Styles
import { Snapshot_s } from 'components/snapshots/snapshot-styles'
import { HeaderIcon_s } from 'components/wrapper/header/header-styles'
// Icons
import { DownloadIcon, ShareIcon, UploadSnapshotIcon } from 'images/icons/tools'

const SnapshotsTable = (props: RouteComponentProps & { type: 'folder' | 'property' }) => {
  const usePrevious = (value) =>{
    const ref = useRef();
    useEffect(() => {
      ref.current = value;
    });
    return ref.current;
  }

  const [data, setData] = useState<object[]>([])
  const [searchFilter, setSearchFilter] = useState<string>("")
  const [snapshotUrl, setSnapshotUrl] = useState<any>("")
  const [showShareConf, setShowShareConf] = useState<boolean>(false)
  const [showDropzone, toggleDropzone] = useState<boolean>(false)
  const [propertyId, setPropertyId] = useState(propertyService.getStoredPropertyId())
  const prevSearch = usePrevious(searchFilter);
  const tableRef = useRef() as React.MutableRefObject<DataTable>
  const folderId = _.last(local("folderPath"))
  const isFolderRoute = props.type == 'folder'

  useEffect(() => {
    if (prevSearch !== "" && searchFilter === "") _.invoke(tableRef, 'current.filter', '', 'contains');
    else if (searchFilter !== "") _.invoke(tableRef, 'current.filter', searchFilter, 'contains');
    else return
  }, [searchFilter])

  useEffect(() => {
    isFolderRoute ? snapshotService.fetchSnapshots(folderId, null) : snapshotService.fetchSnapshots(folderId, propertyId)
  }, [folderId, propertyId])

  useEffect(() => {
    keyService.stream().subscribe(k => {
      if (k.chord === chord.ESC && showDropzone) toggleDropzone(false)
    })
  }, [showDropzone])

  useEffect(() => {
    const snapshotSubscription = snapshotService.stream().subscribe(data => {
      api_match(
        data,
        init  => setData(init.value),
        ok    => {
          setData(ok.value)
        },
        err   => {
          setData(err.value) // backout any optimistic updates
        },
        fetch => {}
      )
    })

    const propertySubscription = propertyService.stream().subscribe((data) => {
      if (data.value.length > 0){
        const property = _.find(data.value, {propertyid: data.current});
        if (property) setPropertyId(property['propertyid'])
      }
    })

    const snapshotEventSubscription = snapshotService.eventStream().subscribe(e => {
      if( e.type === "SNAPSHOT_CREATED"){
        setTimeout(() => {
          isFolderRoute ? snapshotService.fetchSnapshots(folderId, null) : snapshotService.fetchSnapshots(folderId, propertyService.getStoredPropertyId())
        }, 500)
      }
    })

    return function cleanup() {
      propertySubscription.unsubscribe()
      snapshotSubscription.unsubscribe()
      snapshotEventSubscription.unsubscribe()
    }
  }, [])

  const rowClassName = (rowData) => {
    return {'' : rowData}
  }

  const Download = (rowData) => {
    return <a className="download-icon" onClick={()=>snapshotService.downloadSnapshot(rowData.data.snapshotid)}><Isvg src={DownloadIcon} /></a>
  }

  const Share = (rowData) => {
    async function share(){
      const snapshotUrl = await snapshotService.shareSnapshot(rowData.data.snapshotid)
      setSnapshotUrl(snapshotUrl)
      setShowShareConf(true)
    }
    return <a className="share-icon" onClick={()=>share()}><Isvg src={ShareIcon} /></a>
  }

  return (
    <div>
      <Toolbar>
        <CreateSnapshot position="top left" type={props.type} />
        <HeaderIcon_s>
          <WithTip position="bottom">
            <span>Upload Snapshot</span>
            <a onClick={()=>toggleDropzone(!showDropzone)}>
            <Isvg src={UploadSnapshotIcon} id="createSnapshotIcon" />
            </a>
          </WithTip>
        </HeaderIcon_s>
      </Toolbar>
      { showDropzone && <UploadSnapshot toggleDropzone={(bool:boolean)=>toggleDropzone(bool)} /> }
      <SearchHeader setSearchFilter={setSearchFilter} />
      <Snapshot_s id='snapshot-table-scrollable-container'>
        <ConfirmationModal
          height={240}
          width={600}
          contentHeight={150}
          show={showShareConf}
          toggleShow={()=>setShowShareConf(!showShareConf)}
          title={"Share Snapshot"}
          confirmText={"Close"}
          message={""}
          confirmFunc={()=>setShowShareConf(false)}
          cancelFunc={()=>setShowShareConf(false)}
          confirmOnly={true}
          closeOnly={true}
         >
          <p>This shareable snapshot link has been copied to your clipboard and will be valid 30 days.<br/> {snapshotUrl}</p>
        </ConfirmationModal>
        { data?.length > 0 && <DataTable
          globalFilter={searchFilter}
          id='snapshot-table'
          ref={tableRef}
          value={data}
          paginator={true}
          rows={20}
          rowsPerPageOptions={[10, 20, 50, 100, 500]}
          scrollable={true}
          paginatorPosition="bottom"
          rowClassName={rowClassName}
          stateStorage="custom"
          customSaveState={state => localStorage.setItem("primereact-table-preferences", JSON.stringify({rows: state.rows}))}
          customRestoreState={() => JSON.parse(localStorage.getItem("primereact-table-preferences")!)}
        >
          <Column header="#" style={{minWidth: "45px", maxWidth: "45px"}}
            body={(rowData, props) => props.rowIndex + 1}
          />
          <Column style={{minWidth: "300px", maxWidth: "300px"}}
            header="Description"
            field="description"
          />
          <Column style={{minWidth: "180px", maxWidth: "180px"}}
            header="Type"
            field="creationtype"
          />
          <Column style={{minWidth: "180px", maxWidth: "180px"}}
            header="Created Date"
            field="createdate"
            body={(props) => moment(new Date(props['createdate'])).format('MM/DD/YYYY hh:mm A')}
          />
          <Column style={{minWidth: "100px", maxWidth: "100px"}}
            header="Download"
            body={(rowData, props) => <Download data={rowData} />}
          />
          <Column style={{minWidth: "100px", maxWidth: "100px"}}
            header="Share"
            body={(rowData, props) => <Share data={rowData} />}
          />
        </DataTable> }
      </Snapshot_s>
    </div>
  )
}

export default SnapshotsTable
